// src/screens/AvisosScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  IoArrowBack,
  IoCheckmarkDoneOutline,
  IoNewspaperOutline,
  IoPersonCircleOutline,
  IoDocumentTextOutline,
  IoDownloadOutline,
  IoImageOutline,
  IoWarningOutline,
  IoCalendarOutline,
  IoConstructOutline,
  IoInformationCircleOutline,
  IoChevronDownOutline,
  IoChevronUpOutline
} from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import './AvisosScreen.css';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';

function AvisosScreen() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [avisoExpandido, setAvisoExpandido] = useState(null);

  useEffect(() => {
    if (!userData?.condominioId) return;

    setLoading(true);
    const q = query(
      collection(db, "avisos"),
      where("condominioId", "==", userData.condominioId),
      orderBy("fechaCreacion", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rawAvisos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fechaCreacion: doc.data().fechaCreacion.toDate()
      }));

      const filteredAvisos = rawAvisos.filter(a => {
        const aud = a.audiencia || 'todos';
        if (userData.rol === 'admin' || userData.rol === 'comite') return true;
        if (userData.rol === 'vigilante') return aud === 'todos' || aud === 'vigilantes';
        return aud === 'todos' || aud === 'residentes';
      });

      // Ordenar: alertas críticas activas primero, luego por fecha desc
      const sorted = [...filteredAvisos].sort((a, b) => {
        const isAActiveAlert = a.esAlerta && a.fechaExpiracionAlerta && (a.fechaExpiracionAlerta.toDate() > new Date());
        const isBActiveAlert = b.esAlerta && b.fechaExpiracionAlerta && (b.fechaExpiracionAlerta.toDate() > new Date());
        if (isAActiveAlert && !isBActiveAlert) return -1;
        if (!isAActiveAlert && isBActiveAlert) return 1;
        return b.fechaCreacion - a.fechaCreacion;
      });

      setAvisos(sorted);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar avisos: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  const handleExpandAviso = async (aviso) => {
    const isExpanded = avisoExpandido === aviso.id;
    setAvisoExpandido(isExpanded ? null : aviso.id);

    // Auto-read notice
    const yaLeido = aviso.leidoPor?.includes(currentUser.uid);
    if (!isExpanded && !yaLeido) {
      try {
        const avisoRef = doc(db, "avisos", aviso.id);
        await updateDoc(avisoRef, {
          leidoPor: arrayUnion(currentUser.uid)
        });
      } catch (e) {
        console.error("Error al marcar automáticamente como leído:", e);
      }
    }
  };

  const getTipoInfo = (aviso, isCriticalAlert) => {
    if (isCriticalAlert) return { label: 'Alerta Crítica', color: '#ef4444', bg: '#fef2f2', border: '#fee2e2', icon: <IoWarningOutline /> };
    switch (aviso.tipo) {
      case 'urgente':
        return { label: 'Urgente', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: <IoWarningOutline /> };
      case 'evento':
        return { label: 'Evento', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: <IoCalendarOutline /> };
      case 'mantenimiento':
        return { label: 'Mantenimiento', color: '#d97706', bg: '#fffbeb', border: '#fef3c7', icon: <IoConstructOutline /> };
      default:
        return { label: 'Informativo', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: <IoInformationCircleOutline /> };
    }
  };

  const renderAviso = (aviso) => {
    const isExpanded = avisoExpandido === aviso.id;
    const yaLeido = aviso.leidoPor?.includes(currentUser.uid);
    const isCriticalAlert = aviso.esAlerta && aviso.fechaExpiracionAlerta && (aviso.fechaExpiracionAlerta.toDate() > new Date());
    const tipoInfo = getTipoInfo(aviso, isCriticalAlert);

    return (
      <div
        key={aviso.id}
        className={`aviso-card-modern ${isExpanded ? 'expanded' : ''} ${isCriticalAlert ? 'critical' : ''}`}
        onClick={() => handleExpandAviso(aviso)}
      >
        <div className="aviso-card-accent-bar" style={{ backgroundColor: tipoInfo.color }} />

        <div className="aviso-card-body">
          <div className="aviso-card-header">
            <div className="aviso-badge-pill" style={{ color: tipoInfo.color, backgroundColor: tipoInfo.bg, borderColor: tipoInfo.border }}>
              {tipoInfo.icon}
              <span>{tipoInfo.label}</span>
            </div>

            <div className="aviso-meta-right">
              <span className="aviso-date-text">
                {moment(aviso.fechaCreacion).format('D [de] MMMM')}
              </span>
              {!yaLeido && <span className="aviso-badge-new">Nuevo</span>}
            </div>
          </div>

          <h3 className="aviso-card-title">
            {aviso.titulo}
          </h3>

          {isExpanded ? (
            <div className="aviso-full-content">
              {aviso.imagenUrl && (
                <div className="aviso-image-container">
                  <img src={aviso.imagenUrl} alt="Aviso" />
                </div>
              )}
              <div className="aviso-full-text">{aviso.contenido}</div>

              {aviso.adjuntos && aviso.adjuntos.length > 0 && (
                <div className="aviso-attachments-section" onClick={(e) => e.stopPropagation()}>
                  <h4>Documentos y Adjuntos ({aviso.adjuntos.length})</h4>
                  <div className="aviso-attachments-list">
                    {aviso.adjuntos.map((file, idx) => (
                      <a
                        key={idx}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aviso-attachment-item"
                      >
                        <div className="aviso-attachment-info">
                          {file.tipo === 'image' ? <IoImageOutline size={18} /> : <IoDocumentTextOutline size={18} />}
                          <span>{file.nombre}</span>
                        </div>
                        <IoDownloadOutline size={16} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="aviso-preview-text">{aviso.contenido}</p>
          )}

          <div className="aviso-card-footer">
            <div className="aviso-author-info">
              <IoPersonCircleOutline size={18} />
              <span>{aviso.creadoPorNombre || 'Administración'}</span>
            </div>

            <div className="aviso-footer-right">
              {aviso.adjuntos && aviso.adjuntos.length > 0 && !isExpanded && (
                <span className="aviso-attachment-badge">
                  <IoDocumentTextOutline size={14} /> {aviso.adjuntos.length}
                </span>
              )}

              {yaLeido && (
                <span style={{ fontSize: '0.78rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '3px', fontWeight: 600 }}>
                  <IoCheckmarkDoneOutline size={16} /> Leído
                </span>
              )}

              <button className="aviso-expand-toggle-btn">
                {isExpanded ? (
                  <><span>Ver menos</span> <IoChevronUpOutline size={14} /></>
                ) : (
                  <><span>Ver más</span> <IoChevronDownOutline size={14} /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/avisos" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Comunidad y Noticias</h1>
                <p>Anuncios importantes</p>
              </div>
              <IoNewspaperOutline className="muro-hero-icon-decor" />
            </div>
          </div>
          <div className="avisos-container">

      {/* Spacer to prevent content jumping under fixed header */
        /* Not strictly needed if we use sticky correctly, but helpful structure */
      }

      {loading ? (
        <p style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Cargando...</p>
      ) : avisos.length === 0 ? (
        <div className="empty-avisos">
          <IoNewspaperOutline size={60} style={{ opacity: 0.5, marginBottom: '15px' }} />
          <h3>No hay avisos recientes</h3>
          <p>Te mantendremos informado de cualquier novedad.</p>
        </div>
      ) : (
        <div className="avisos-grid">
          {avisos.map(renderAviso)}
        </div>
      )}
        </div>
        </div>
        {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
        <KondoBottomNav activeRoute="/avisos" />
      </main>
    </div>
  );
}

export default AvisosScreen;